/** LATAM location/currency catalog. Country names are persisted API values.
 * Zones: https://data.iana.org/time-zones/tzdb/zone.tab (2026-09-10).
 * Currencies: Unicode CLDR supplementalData.xml (2026-09-10); Panama uses USD.
 * Service currencies only: this catalog does not define subscription prices.
 * Canonical copy: Cyclone src/constants/latam-markets.ts. Run scripts/sync-latam-markets.mjs.
 */
export interface LatamMarket {
  country: string;
  iso: string;
  locale: string;
  currency: string;
  regionLabel: string;
  timezones: string[];
}

export const LATAM_MARKETS: readonly LatamMarket[] = [
  {
    country: 'Argentina',
    iso: 'AR',
    locale: 'es-AR',
    currency: 'ARS',
    regionLabel: 'provincia',
    timezones: [
      'America/Argentina/Buenos_Aires',
      'America/Argentina/Cordoba',
      'America/Argentina/Salta',
      'America/Argentina/Jujuy',
      'America/Argentina/Tucuman',
      'America/Argentina/Catamarca',
      'America/Argentina/La_Rioja',
      'America/Argentina/San_Juan',
      'America/Argentina/Mendoza',
      'America/Argentina/San_Luis',
      'America/Argentina/Rio_Gallegos',
      'America/Argentina/Ushuaia'
    ]
  },
  {
    country: 'Uruguay',
    iso: 'UY',
    locale: 'es-UY',
    currency: 'UYU',
    regionLabel: 'departamento',
    timezones: ['America/Montevideo']
  },
  {
    country: 'Chile',
    iso: 'CL',
    locale: 'es-CL',
    currency: 'CLP',
    regionLabel: 'región',
    timezones: ['America/Santiago', 'America/Coyhaique', 'America/Punta_Arenas', 'Pacific/Easter']
  },
  {
    country: 'México',
    iso: 'MX',
    locale: 'es-MX',
    currency: 'MXN',
    regionLabel: 'estado',
    timezones: [
      'America/Mexico_City',
      'America/Cancun',
      'America/Merida',
      'America/Monterrey',
      'America/Matamoros',
      'America/Chihuahua',
      'America/Ciudad_Juarez',
      'America/Ojinaga',
      'America/Mazatlan',
      'America/Bahia_Banderas',
      'America/Hermosillo',
      'America/Tijuana'
    ]
  },
  {
    country: 'Venezuela',
    iso: 'VE',
    locale: 'es-VE',
    currency: 'VES',
    regionLabel: 'estado',
    timezones: ['America/Caracas']
  },
  {
    country: 'Bolivia',
    iso: 'BO',
    locale: 'es-BO',
    currency: 'BOB',
    regionLabel: 'departamento',
    timezones: ['America/La_Paz']
  },
  {
    country: 'Brasil',
    iso: 'BR',
    locale: 'pt-BR',
    currency: 'BRL',
    regionLabel: 'estado',
    timezones: [
      'America/Noronha',
      'America/Belem',
      'America/Fortaleza',
      'America/Recife',
      'America/Araguaina',
      'America/Maceio',
      'America/Bahia',
      'America/Sao_Paulo',
      'America/Campo_Grande',
      'America/Cuiaba',
      'America/Santarem',
      'America/Porto_Velho',
      'America/Boa_Vista',
      'America/Manaus',
      'America/Eirunepe',
      'America/Rio_Branco'
    ]
  },
  {
    country: 'Colombia',
    iso: 'CO',
    locale: 'es-CO',
    currency: 'COP',
    regionLabel: 'departamento',
    timezones: ['America/Bogota']
  },
  {
    country: 'Costa Rica',
    iso: 'CR',
    locale: 'es-CR',
    currency: 'CRC',
    regionLabel: 'provincia',
    timezones: ['America/Costa_Rica']
  },
  {
    country: 'Cuba',
    iso: 'CU',
    locale: 'es-CU',
    currency: 'CUP',
    regionLabel: 'provincia',
    timezones: ['America/Havana']
  },
  {
    country: 'República Dominicana',
    iso: 'DO',
    locale: 'es-DO',
    currency: 'DOP',
    regionLabel: 'provincia',
    timezones: ['America/Santo_Domingo']
  },
  {
    country: 'Ecuador',
    iso: 'EC',
    locale: 'es-EC',
    currency: 'USD',
    regionLabel: 'provincia',
    timezones: ['America/Guayaquil', 'Pacific/Galapagos']
  },
  {
    country: 'El Salvador',
    iso: 'SV',
    locale: 'es-SV',
    currency: 'USD',
    regionLabel: 'departamento',
    timezones: ['America/El_Salvador']
  },
  {
    country: 'Guatemala',
    iso: 'GT',
    locale: 'es-GT',
    currency: 'GTQ',
    regionLabel: 'departamento',
    timezones: ['America/Guatemala']
  },
  {
    country: 'Haití',
    iso: 'HT',
    locale: 'fr-HT',
    currency: 'HTG',
    regionLabel: 'departamento',
    timezones: ['America/Port-au-Prince']
  },
  {
    country: 'Honduras',
    iso: 'HN',
    locale: 'es-HN',
    currency: 'HNL',
    regionLabel: 'departamento',
    timezones: ['America/Tegucigalpa']
  },
  {
    country: 'Nicaragua',
    iso: 'NI',
    locale: 'es-NI',
    currency: 'NIO',
    regionLabel: 'departamento',
    timezones: ['America/Managua']
  },
  {
    country: 'Panamá',
    iso: 'PA',
    locale: 'es-PA',
    currency: 'USD',
    regionLabel: 'provincia',
    timezones: ['America/Panama']
  },
  {
    country: 'Paraguay',
    iso: 'PY',
    locale: 'es-PY',
    currency: 'PYG',
    regionLabel: 'departamento',
    timezones: ['America/Asuncion']
  },
  {
    country: 'Perú',
    iso: 'PE',
    locale: 'es-PE',
    currency: 'PEN',
    regionLabel: 'región',
    timezones: ['America/Lima']
  },
  {
    country: 'Belice',
    iso: 'BZ',
    locale: 'es-BZ',
    currency: 'BZD',
    regionLabel: 'distrito',
    timezones: ['America/Belize']
  },
  {
    country: 'Guyana',
    iso: 'GY',
    locale: 'en-GY',
    currency: 'GYD',
    regionLabel: 'región',
    timezones: ['America/Guyana']
  },
  {
    country: 'Surinam',
    iso: 'SR',
    locale: 'nl-SR',
    currency: 'SRD',
    regionLabel: 'distrito',
    timezones: ['America/Paramaribo']
  },
  {
    country: 'Puerto Rico',
    iso: 'PR',
    locale: 'es-PR',
    currency: 'USD',
    regionLabel: 'municipio',
    timezones: ['America/Puerto_Rico']
  },
  {
    country: 'Guayana Francesa',
    iso: 'GF',
    locale: 'fr-GF',
    currency: 'EUR',
    regionLabel: 'municipio',
    timezones: ['America/Cayenne']
  },
  {
    country: 'Guadalupe',
    iso: 'GP',
    locale: 'fr-GP',
    currency: 'EUR',
    regionLabel: 'municipio',
    timezones: ['America/Guadeloupe']
  },
  {
    country: 'Martinica',
    iso: 'MQ',
    locale: 'fr-MQ',
    currency: 'EUR',
    regionLabel: 'municipio',
    timezones: ['America/Martinique']
  }
];

export const getLatamMarket = (country?: string | null): LatamMarket | undefined =>
  LATAM_MARKETS.find((market) => market.country === country);
export const timezoneBelongsToCountry = (country: string, timezone: string): boolean => {
  const zones = getLatamMarket(country)?.timezones;
  if (!zones) return false;
  try {
    const canonical = new Intl.DateTimeFormat('es', { timeZone: timezone }).resolvedOptions().timeZone;
    return zones.some((zone) => {
      try {
        return new Intl.DateTimeFormat('es', { timeZone: zone }).resolvedOptions().timeZone === canonical;
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
};
